import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {ApiClientService} from '../../../services/api-client.service';
import {UrlService} from '../../../services/url.service';
import {ConstantsService} from '../../../services/constants.service';
import {ReportGeneratorService} from 'app/services/report-generator.service';

declare const toastr: any;
declare const $: any;
declare const swal: any;
declare const moment: any;

@Component({
    selector: 'runtime-conditions-modal',
    templateUrl: './runtime-conditions-modal.component.html',
    styleUrls: ['./runtime-conditions-modal.component.scss']
})
export class RuntimeConditionsModalComponent implements OnInit {
    @ViewChild('form') form;
    showModal = false;
    disabledSubmit = false;
    data: any = null;
    conditions = [];
    section: any = null;
    showSpinner = false;
    possibleValues = [];
    name = '';

    constructor(
        public api: ApiClientService,
        public reportGeneratorService: ReportGeneratorService,
        public urlService: UrlService
    ) {}

    ngOnInit() {
        this.initData();
        setTimeout( () => this.showModal = true, 300);
        this.urlService.modalOpened.emit();
    }

    initData() {
        this.conditions = this.reportGeneratorService.templateInTemplateModal.conditions.filter(cond => cond.isRuntime);
        this.getSections();
    }

    hideModal() {
        if (this.form.dirty && this.form.touched) {
            swal(ConstantsService.getSwalConfig('Close without saving?', 'Do you want to delete unsaved data?'),
                () => {
                    this.reportGeneratorService.showRuntimeConditionsModal = false;
                    this.reportGeneratorService.templateInTemplateModal = null;
                    this.urlService.modalClosed.emit();
                }
            );
        } else {
            this.reportGeneratorService.showRuntimeConditionsModal = false;
            this.reportGeneratorService.templateInTemplateModal = null;
            this.urlService.modalClosed.emit();
        }
    }

    onSubmit(form: NgForm) {
        if(!this.name || !this.name.trim()) {
            toastr.warning('Name should not be blank.');
            return;
        }

        const url = `/v1/reports/template-${this.reportGeneratorService.templateInTemplateModal.id}`;
        const conditions = this.conditions.map( c => ({
            key: c.key,
            condition: c.condition,
            values: this.formatValues(c),
            isRuntime: c.isRuntime
        }));
        const data = {
            conditions: conditions,
            name: this.name
        };
        this.disabledSubmit = true;
        this.api.post('domainReportGenerator', `${url}`, data).subscribe(
            res => {
                //go to the reports tab. Kill and launch the refresh function
                this.disabledSubmit = false;
                toastr.success('Report was successfully ran. Please wait for it to be prepared.');
                this.urlService.queryParams['order-by'] = null;
                this.urlService.setQueryParams('allReports');
                this.reportGeneratorService.tabId = 'allReports';
                this.form.reset();
                this.hideModal();
            },
            error => {
                this.disabledSubmit = false;
                this.api.errorHandler(error, 'Failed to run Report');
            }
        );
    }

    getSections() {
        this.showSpinner = true;
        const url = '/v1/report_sections.json';
        this.api.get('domainReportGenerator', url).subscribe(
            (res) => {
                const body = JSON.parse(res._body);
                const chosenSection = JSON.parse(JSON.stringify(body.entities.filter(section => section.id == this.reportGeneratorService.templateInTemplateModal.section)[0]));
                this.section = chosenSection;
                this.conditions.forEach( cond => {
                    const sectionField = chosenSection.fields.find(field => field.key == cond.key);
                    if(sectionField)
                        cond.inputType = sectionField.conditions.find(c => c.id == cond.condition).inputType;
                    if(cond.inputType == 'buildings')
                        cond.values = cond.values.map(v => ({id: v.toString(), itemName: this.getBuildingNameById(v)}));
                    else if(cond.inputType == 'string') {
                        if(sectionField.possibleValues && sectionField.possibleValues.length > 0) {
                            cond.inputType = 'possibleValues';
                            cond.values = cond.values.map(v => ({id: v, itemName: v}));
                            cond.possibleValues = this.getPossibleValues(cond.key);
                        }
                    } else if(cond.inputType == 'date') {
                        cond.values = cond.values.map(v => (
                            // Replacing '-' with '/' for proper timezone initialization for Date() object in darepicker
                            new Date(v.replace(/-/g, '/'))
                        ));
                    }
                });
                this.showSpinner = false;
            },
            (error) => {
                this.showSpinner = false;
                this.api.errorHandler(error, 'Failed to get Sections');
            }
        );
    }

    getConditions(key): Array<any> {
        let conditions = [];
        if(this.section)
            conditions = this.section.fields.find(cond => key == cond.key).conditions;
        return conditions;
    }

    getKeyName(key): string {
        return this.section ? this.section.fields.find(cond => key == cond.key).name : '';
    }

    onConditionChange(event, c) {
        c.values[0] = '';
        const field = this.section.fields.find(cond => c.key == cond.key);
        const conditions = this.getConditions(c.key);
        c.inputType = conditions.find(cond => cond.id == c.condition).inputType;
        if(c.inputType == 'string' && field.possibleValues && field.possibleValues.length) {
            c.inputType = 'possibleValues';
            c.possibleValues = this.getPossibleValues(c.key);
        }
    }

    getBuildingNameById(id): string {
        let name = '';

        this.reportGeneratorService.buildingsForMultiselect.forEach(building => {
            if (building.id == id)
                name = building.name;
        });

        return name;
    }

    getPossibleValues(key) {
        const field = this.section.fields.find(field => field.key == key);
        if(field && field.possibleValues)
            return field.possibleValues.map(v => ({
                        id: v,
                        itemName: v
                    }));
        else
            return [];
    }

    formatValues(c) {
        let values = undefined;
        if(c.inputType) {
            if(c.inputType == 'buildings')
                values = c.values.map(v => (+v.id));
            else if(c.inputType == 'possibleValues')
                values = c.values.map(v => (v.id));
            else if(c.inputType == 'date')
                values = c.values.map(v => (moment(v).format('YYYY-MM-DD')));
            else
                values = c.values;
        }
        return values;
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        setTimeout(() => this.hideModal(), 100);
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/report-generator/runtime-conditions-modal/runtime-conditions-modal.component.ts